import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, ArrowRight } from "lucide-react";
import { LOGO_URL } from "@/data/site";
import { CATEGORIES, getFormationsByCategory } from "@/data/formations";
import { useLead } from "@/components/common/LeadModal";

const NAV = [
  { label: "Le Groupe", to: "/le-groupe" },
  { label: "Nos formations", to: "/nos-formations", mega: true },
  { label: "Financements", to: "/financements" },
  { label: "Nos centres", to: "/nos-centres" },
  { label: "Actualités & Blog", to: "/blog" },
  { label: "Contact", to: "/contact" },
];

export default function Header() {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { openLead } = useLead();

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-brand-blue" data-testid="site-header">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-5 py-3 lg:px-8">
        {/* Logo */}
        <Link to="/" className="flex items-center rounded-lg bg-white px-2.5 py-1.5" data-testid="header-logo">
          <img src={LOGO_URL} alt="EDUCASER GROUP" className="h-8 w-auto object-contain sm:h-9" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex" onMouseLeave={() => setMegaOpen(false)}>
          {NAV.map((item) =>
            item.mega ? (
              <div key={item.to} onMouseEnter={() => setMegaOpen(true)}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-1 px-3 py-2 text-[15px] font-semibold transition-colors ${
                      isActive ? "text-brand-red-light" : "text-white hover:text-brand-red-light"
                    }`
                  }
                  data-testid="nav-nos-formations"
                >
                  {item.label}
                  <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
                </NavLink>
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-3 py-2 text-[15px] font-semibold transition-colors ${
                    isActive ? "text-brand-red-light" : "text-white hover:text-brand-red-light"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/'/g, "")}`}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Right CTAs (Nexa-style pills) */}
        <div className="hidden items-center gap-2.5 lg:flex">
          <button
            onClick={() => openLead("inscription")}
            className="rounded-full bg-brand-red px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-red-light"
            data-testid="header-cta-inscription"
          >
            S'inscrire
          </button>
          <button
            onClick={() => openLead("rappel")}
            className="rounded-full bg-white px-5 py-2.5 text-sm font-bold text-brand-blue transition-transform hover:-translate-y-0.5"
            data-testid="header-cta-rappel"
          >
            Être rappelé
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="text-white lg:hidden"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
        </button>
      </div>

      {/* Mega menu */}
      <AnimatePresence>
        {megaOpen && (
          <motion.div
            className="absolute inset-x-0 top-full hidden lg:block"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22 }}
            onMouseEnter={() => setMegaOpen(true)}
            onMouseLeave={() => setMegaOpen(false)}
            data-testid="mega-menu"
          >
            <div className="mx-auto max-w-[1400px] px-8">
              <div className="grid grid-cols-3 gap-8 rounded-b-2xl bg-white p-8 shadow-[0_25px_50px_-12px_rgba(0,35,102,0.2)]">
                {CATEGORIES.map((cat, i) => (
                  <motion.div
                    key={cat.slug}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <Link
                      to={`/nos-formations/${cat.slug}`}
                      className="mb-3 flex items-center gap-2 border-l-4 border-brand-red pl-3 font-display text-base font-extrabold uppercase tracking-tight text-brand-blue hover:text-brand-red"
                    >
                      {cat.title}
                    </Link>
                    <ul className="space-y-1">
                      {getFormationsByCategory(cat.slug).map((f) => (
                        <li key={f.slug}>
                          <Link
                            to={`/nos-formations/${cat.slug}/${f.slug}`}
                            className="block rounded-lg px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 hover:text-brand-blue"
                          >
                            {f.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="max-h-[calc(100vh-64px)] overflow-y-auto bg-white lg:hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-5 py-4">
              <NavLink to="/" end className="block rounded-xl px-3 py-3 text-base font-semibold text-brand-blue">
                Accueil
              </NavLink>
              {NAV.map((item) => (
                <div key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `block rounded-xl px-3 py-3 text-base font-semibold ${
                        isActive ? "bg-slate-50 text-brand-red" : "text-brand-blue"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                  {item.mega && (
                    <div className="ml-3 border-l border-slate-200 pl-3">
                      {CATEGORIES.map((cat) => (
                        <div key={cat.slug} className="py-1">
                          <Link to={`/nos-formations/${cat.slug}`} className="block py-1 text-sm font-bold text-brand-red">
                            {cat.title}
                          </Link>
                          {getFormationsByCategory(cat.slug).map((f) => (
                            <Link key={f.slug} to={`/nos-formations/${cat.slug}/${f.slug}`} className="block py-1 text-sm text-slate-500">
                              {f.title}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex flex-col gap-2 pt-3">
                <button onClick={() => openLead("inscription")} className="rounded-full bg-brand-red px-5 py-3 font-bold text-white" data-testid="mobile-cta-inscription">
                  S'inscrire
                </button>
                <button onClick={() => openLead("rappel")} className="rounded-full border-2 border-brand-blue px-5 py-3 font-bold text-brand-blue">
                  Être rappelé
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
